import React, { useEffect, useState } from 'react';
import { Toast as ToastType } from '../hooks/useToast';

interface ToastProps {
  toast: ToastType;
  onRemove: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleRemove = () => {
    setIsVisible(false);
    setTimeout(() => onRemove(toast.id), 300);
  };

  const getToastStyles = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-[#F2F9ED] border-[#80BC5C] text-[#2F4E1F]';
      case 'error':
        return 'bg-[#FDECEF] border-[#E64853] text-[#611720]';
      case 'warning':
        return 'bg-[#FFF6E9] border-[#EE8435] text-[#5F3A16]';
      case 'info':
      default:
        return 'bg-[#E6F0FB] border-[#00366D] text-[#00366D]';
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  return (
    <div
      className={`
        flex items-center p-4 mb-3 rounded-xl border shadow-lg shadow-[rgba(0,54,109,0.18)]
        transition-all duration-300 ease-out
        ${getToastStyles()}
        ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'}
      `}
      role="alert"
    >
      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center mr-3 font-bold text-xl">
        {getIcon()}
      </div>
      <div className="flex-1 text-sm font-medium">
        {toast.message}
      </div>
      <button
        onClick={handleRemove}
        className="flex-shrink-0 ml-4 text-[var(--color-text-secondary)] hover:text-accent transition-colors"
        aria-label="Sluiten"
      >
        ✕
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: ToastType[];
  onRemove: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  return (
    <div
      className="fixed top-4 right-4 z-50 w-full max-w-sm pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="pointer-events-auto">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onRemove={onRemove} />
        ))}
      </div>
    </div>
  );
};

export default Toast;
